/**
 * Eigenbau: MCP-Zugänge — Token erzeugen und die Konfiguration abholen.
 *
 * Wer cubic studio an Claude Code, Codex oder einen anderen Client hängen
 * wollte, musste sich ein Authentik-Zugriffstoken aus der Sitzung fischen. Das
 * läuft binnen Stunden ab und ist ein Sitzungsgeheimnis — in einer
 * Konfigurationsdatei auf der Platte beides falsch.
 *
 * Hier entsteht stattdessen ein eigener Zugang mit ausdrücklichen Rechten. Das
 * Geheimnis erscheint **einmal**, direkt daneben die fertige Konfiguration zum
 * Kopieren. Danach steht es nirgends mehr: Gespeichert ist nur sein Hash.
 *
 * Die Adresse kommt aus `mcpAdresse()` und nicht aus einem Eingabefeld — so
 * steht in der kopierten Zeile garantiert der Server, mit dem diese Oberfläche
 * gerade spricht.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BEREICHSNAMEN,
  vorgabesatz,
  type Werkzeugbereich,
} from "@cubic/shared";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Leerzustand } from "@/components/Leerzustand";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { api, mcpAdresse } from "@/lib/api";
import { fehlerMeldung } from "@/lib/haken";
import type { Meldung } from "@/lib/meldung";
import { cx, focusRing } from "@/lib/utils";
import {
  EinstellungenHuelle,
  EinstellungsAbschnitt,
  LadenZeile,
} from "./gemeinsam";

function datum(wert: string | Date | null | undefined): string {
  if (!wert) return "nie";
  return new Date(wert).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Kopierknopf, der kurz bestätigt — sonst weiß niemand, ob es geklappt hat. */
function Kopierknopf({ text, was }: { text: string; was: string }) {
  const [kopiert, setzeKopiert] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setzeKopiert(true);
          setTimeout(() => setzeKopiert(false), 2000);
        });
      }}
      className={cx(
        "border border-linie2 px-2.5 py-1 font-mono text-[11px] text-tinte2",
        "transition-colors duration-150 ease-aus hover:border-linie hover:text-tinte",
        focusRing,
      )}
      aria-label={`${was} kopieren`}
    >
      {kopiert ? "kopiert" : "kopieren"}
    </button>
  );
}

function Kopierblock({
  titel,
  hinweis,
  inhalt,
}: {
  titel: string;
  hinweis: string;
  inhalt: string;
}) {
  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <Mikrolabel grad={11} ton="laut" betont>
          {titel}
        </Mikrolabel>
        <Kopierknopf text={inhalt} was={titel} />
      </div>
      <p className="mt-1 text-[12.5px] font-light leading-relaxed text-tinte3">
        {hinweis}
      </p>
      {/*
        Dunkle Tafel — und deshalb mit eigener Tinte. `--tinte*` ist für helles
        Papier gedacht; auf einer Tafel wäre sie Schrift auf Schrift.
      */}
      <pre className="mt-2 overflow-x-auto bg-tafel px-4 py-3 font-mono text-[11.5px] leading-[1.7] text-tafel-tinte">
        {inhalt}
      </pre>
    </div>
  );
}

export function EinstellungenZugaenge() {
  const client = useQueryClient();
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);
  const [name, setzeName] = useState("");
  const [auswahl, setzeAuswahl] = useState<string[]>(() => vorgabesatz("voll"));
  const [offenerBereich, setzeOffenerBereich] = useState<string | null>(null);
  /** Das Geheimnis — nur im Speicher, nur bis zum nächsten Erzeugen. */
  const [frisch, setzeFrisch] = useState<{ name: string; token: string } | null>(
    null,
  );

  const zugaenge = useQuery({
    queryKey: ["cubic", "mcpZugaenge"],
    queryFn: () => api.mcpZugaenge.liste.query(),
  });
  const werkzeuge = useQuery({
    queryKey: ["cubic", "mcpZugaenge", "werkzeuge"],
    queryFn: () => api.mcpZugaenge.werkzeuge.query(),
  });

  const nachBereich = useMemo(() => {
    const gruppen = new Map<
      string,
      { name: string; art: "lesen" | "schreiben" }[]
    >();
    for (const w of werkzeuge.data ?? []) {
      const liste = gruppen.get(w.bereich) ?? [];
      liste.push({ name: w.name, art: w.art });
      gruppen.set(w.bereich, liste);
    }
    return [...gruppen.entries()];
  }, [werkzeuge.data]);

  const neuLaden = () =>
    client.invalidateQueries({ queryKey: ["cubic", "mcpZugaenge"] });

  const erzeugen = useMutation({
    mutationFn: () =>
      api.mcpZugaenge.erzeugen.mutate({ name: name.trim(), werkzeuge: auswahl }),
    onSuccess: async (ergebnis) => {
      setzeMeldung(null);
      setzeFrisch({ name: name.trim(), token: ergebnis.token });
      setzeName("");
      await neuLaden();
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const widerrufen = useMutation({
    mutationFn: (id: string) => api.mcpZugaenge.widerrufen.mutate({ id }),
    onSuccess: async () => {
      setzeMeldung(null);
      await neuLaden();
    },
    onError: (e) => setzeMeldung(fehlerMeldung(e)),
  });

  const adresse = mcpAdresse();
  const zugangsname = frisch?.name ?? "cubic-studio";
  const befehl = `claude mcp add --transport http cubic-studio ${adresse} --header "Authorization: Bearer ${frisch?.token ?? ""}"`;
  const jsonBlock = JSON.stringify(
    {
      mcpServers: {
        "cubic-studio": {
          type: "http",
          url: adresse,
          headers: { Authorization: `Bearer ${frisch?.token ?? ""}` },
        },
      },
    },
    null,
    2,
  );

  const umschalten = (werkzeugname: string) =>
    setzeAuswahl((vorher) =>
      vorher.includes(werkzeugname)
        ? vorher.filter((n) => n !== werkzeugname)
        : [...vorher, werkzeugname],
    );

  const bereichUmschalten = (
    eintraege: { name: string; art: string }[],
    art: "lesen" | "schreiben",
  ) => {
    const betroffen = eintraege.filter((e) => e.art === art).map((e) => e.name);
    const alleDrin = betroffen.every((n) => auswahl.includes(n));
    setzeAuswahl((vorher) =>
      alleDrin
        ? vorher.filter((n) => !betroffen.includes(n))
        : [...new Set([...vorher, ...betroffen])],
    );
  };

  const liste = zugaenge.data ?? [];

  return (
    <EinstellungenHuelle
      aktiv="zugaenge"
      beschreibung="Zugänge für Claude Code, Codex und andere MCP-Clients."
    >
      {/*
        Der frische Zugang steht oben und verdrängt alles andere — er ist das
        Einzige auf dieser Seite, das man verpassen kann.
      */}
      {frisch ? (
        <EinstellungsAbschnitt
          titel={`Zugang „${zugangsname}“ ist da`}
          beschreibung="Das Token siehst du genau einmal. Gespeichert ist nur sein Hash — geht es verloren, wird ein neuer Zugang angelegt, nicht dieser ausgelesen."
          aktion={
            <Button variant="secondary" onClick={() => setzeFrisch(null)}>
              Fertig
            </Button>
          }
        >
          <div className="space-y-6">
            <Kopierblock
              titel="Claude Code"
              hinweis="Einmal im Terminal ausführen. Danach steht der Server in dieser Sitzung bereit — MCP-Server werden beim Start verbunden, ein laufendes Gespräch sieht ihn also erst nach einem neuen Start."
              inhalt={befehl}
            />
            <Kopierblock
              titel="Codex, Claude Desktop, Cursor"
              hinweis="In die Konfigurationsdatei des Programms einfügen. Alle drei lesen dieselbe Form."
              inhalt={jsonBlock}
            />
            <Kopierblock
              titel="Alles andere"
              hinweis="Adresse und Kopfzeile einzeln, für Clients mit eigenem Formular."
              inhalt={`${adresse}\nAuthorization: Bearer ${frisch.token}`}
            />
          </div>
        </EinstellungsAbschnitt>
      ) : null}

      <EinstellungsAbschnitt
        titel="Zugänge"
        beschreibung="Ein Zugang spricht in deinem Namen, kann aber nur weniger als du: Rollen und Rechte werden bei jedem Aufruf frisch geprüft. Wird dir ein Projektrecht entzogen, wirkt das sofort."
      >
        {meldung ? <Fehlerzeile meldung={meldung} /> : null}

        {zugaenge.isLoading ? (
          <LadenZeile text="Lade Zugänge …" />
        ) : liste.length === 0 ? (
          <Leerzustand
            titel="Noch kein Zugang."
            hinweis="Lege einen an, um cubic studio in Claude Code, Codex oder einem anderen Programm zu benutzen."
          />
        ) : (
          <ul role="list" className="space-y-px">
            {liste.map((zugang) => (
              <li
                key={zugang.id}
                className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b border-linie py-3.5"
              >
                <span
                  className={cx(
                    "text-[14px]",
                    zugang.widerrufenAm ? "text-tinte4 line-through" : "text-tinte",
                  )}
                >
                  {zugang.name}
                </span>
                {zugang.nurLesend ? (
                  <Mikrolabel grad={11}>nur lesend</Mikrolabel>
                ) : null}
                <span className="ml-auto flex flex-wrap items-baseline gap-x-4">
                  <Mikrolabel grad={11} zahl>
                    {zugang.rechte.lesen} lesend, {zugang.rechte.schreiben}{" "}
                    schreibend
                  </Mikrolabel>
                  <Mikrolabel grad={11} zahl>
                    zuletzt {datum(zugang.letztGenutztAm)}
                  </Mikrolabel>
                  {zugang.widerrufenAm ? (
                    <Mikrolabel grad={11}>widerrufen</Mikrolabel>
                  ) : (
                    <button
                      type="button"
                      onClick={() => widerrufen.mutate(zugang.id)}
                      disabled={widerrufen.isPending}
                      className={cx(
                        "font-mono text-[11px] text-tinte4 hover:text-akzent",
                        focusRing,
                      )}
                    >
                      widerrufen
                    </button>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </EinstellungsAbschnitt>

      <EinstellungsAbschnitt
        titel="Neuer Zugang"
        beschreibung="Ein Name, ein Rechteumfang, fertig. Für den Normalfall reicht der Vorgabesatz."
      >
        <div className="space-y-5">
          <div>
            <Label htmlFor="zugang-name">Wofür ist der Zugang</Label>
            <Input
              id="zugang-name"
              className="mt-1.5"
              placeholder="Claude Code auf dem Laptop"
              value={name}
              onChange={(e) => setzeName(e.target.value)}
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              onClick={() => setzeAuswahl(vorgabesatz("voll"))}
            >
              Alles
            </Button>
            <Button
              variant="secondary"
              onClick={() => setzeAuswahl(vorgabesatz("nur_lesen"))}
            >
              Nur lesen
            </Button>
            <Button variant="secondary" onClick={() => setzeAuswahl([])}>
              Nichts
            </Button>
          </div>

          <div>
            <Rubrik anzahl={`${auswahl.length} von ${werkzeuge.data?.length ?? 0}`}>
              Werkzeuge
            </Rubrik>
            <ul role="list" className="space-y-px">
              {nachBereich.map(([bereich, eintraege]) => {
                const offen = offenerBereich === bereich;
                const gewaehlt = eintraege.filter((e) =>
                  auswahl.includes(e.name),
                ).length;
                return (
                  <li key={bereich} className="border-b border-linie">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5">
                      <button
                        type="button"
                        aria-expanded={offen}
                        onClick={() =>
                          setzeOffenerBereich(offen ? null : bereich)
                        }
                        className={cx("text-left text-[13.5px] text-tinte", focusRing)}
                      >
                        {BEREICHSNAMEN[bereich as Werkzeugbereich] ?? bereich}
                      </button>
                      <Mikrolabel grad={11} zahl>
                        {gewaehlt}/{eintraege.length}
                      </Mikrolabel>
                      <span className="ml-auto flex gap-1.5">
                        {(["lesen", "schreiben"] as const).map((art) => (
                          <button
                            key={art}
                            type="button"
                            onClick={() => bereichUmschalten(eintraege, art)}
                            className={cx(
                              "border border-linie2 px-2 py-0.5 font-mono text-[11px] text-tinte3",
                              "hover:border-linie hover:text-tinte",
                              focusRing,
                            )}
                          >
                            {art}
                          </button>
                        ))}
                      </span>
                    </div>
                    {offen ? (
                      <div className="flex flex-wrap gap-1.5 pb-3">
                        {eintraege.map((eintrag) => {
                          const aktiv = auswahl.includes(eintrag.name);
                          return (
                            <button
                              key={eintrag.name}
                              type="button"
                              aria-pressed={aktiv}
                              onClick={() => umschalten(eintrag.name)}
                              className={cx(
                                "border px-2.5 py-1 font-mono text-[11px] transition-colors duration-150 ease-aus",
                                aktiv
                                  ? "border-akzent text-tinte"
                                  : "border-linie2 text-tinte4 hover:border-linie hover:text-tinte2",
                                focusRing,
                              )}
                            >
                              {eintrag.name}
                              {eintrag.art === "schreiben" ? " ✎" : ""}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>

          <Button
            onClick={() => erzeugen.mutate()}
            disabled={
              erzeugen.isPending || !name.trim() || auswahl.length === 0
            }
            isLoading={erzeugen.isPending}
            loadingText="Erzeuge …"
          >
            Zugang erzeugen
          </Button>
        </div>
      </EinstellungsAbschnitt>
    </EinstellungenHuelle>
  );
}
